/**
 * Control Portal - Organization Location Management
 * Tree-based location hierarchy builder
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Plus, ChevronRight, ChevronDown, 
  Edit2, Trash2, Loader2, Building, Store, Warehouse
} from 'lucide-react';

const LOCATION_TYPES = [
  { value: 'country', label: 'Country', icon: '🌍' },
  { value: 'state', label: 'State', icon: '🗺️' },
  { value: 'city', label: 'City', icon: '🏙️' },
  { value: 'region', label: 'Region', icon: '📍' },
  { value: 'warehouse', label: 'Warehouse', icon: '🏭' },
  { value: 'hub', label: 'Hub', icon: '🔄' },
  { value: 'store', label: 'Store', icon: '🏪' },
];

const getTypeIcon = (type) => {
  const found = LOCATION_TYPES.find(t => t.value === type);
  return found?.icon || '📍';
};

// Location Tree Node Component
function LocationNode({ location, locations, level = 0, onEdit, onDelete, onAddChild }) {
  const [expanded, setExpanded] = useState(level < 2);
  const children = locations.filter(l => l.parent_id === location.id);
  const hasChildren = children.length > 0;

  return (
    <div className="select-none" data-testid={`location-node-${location.id}`}>
      <div
        className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-slate-800/50 transition-colors group`}
        style={{ marginLeft: level * 20 }}
      >
        {hasChildren ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-5 h-5 flex items-center justify-center text-slate-500 hover:text-white"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <span className="w-5" />
        )}
        
        <span className="text-lg">{getTypeIcon(location.type)}</span>
        
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{location.name}</p>
          <p className="text-slate-500 text-xs">
            {location.code && <span className="font-mono">{location.code} · </span>}
            {location.city && <span>{location.city}, </span>}
            {location.state || location.type}
          </p>
        </div>

        <Badge variant="outline" className="border-slate-700 text-xs capitalize">
          {location.type}
        </Badge>

        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-slate-400 hover:text-green-400"
            onClick={() => onAddChild(location)}
            title="Add child location"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-slate-400 hover:text-blue-400"
            onClick={() => onEdit(location)}
            title="Edit location"
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-slate-400 hover:text-red-400"
            onClick={() => onDelete(location)}
            title="Delete location"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {expanded && hasChildren && (
        <div>
          {children.map(child => (
            <LocationNode
              key={child.id}
              location={child}
              locations={locations}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TenantLocationsPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [locations, setLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [parentLocation, setParentLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'store',
    parent_id: null,
    address: '',
    city: '',
    state: '',
    country: 'India',
    pin_code: '',
    latitude: '',
    longitude: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantRes, locationsRes] = await Promise.all([
        controlApi.getTenant(tenantId),
        controlApi.getLocations(tenantId, { limit: 500 })
      ]);
      setTenant(tenantRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateDialog = (parent = null) => {
    setEditingLocation(null);
    setParentLocation(parent);
    setFormData({
      name: '',
      code: '',
      type: parent ? getChildType(parent.type) : 'region',
      parent_id: parent?.id || null,
      address: '',
      city: '',
      state: parent?.state || '',
      country: parent?.country || 'India',
      pin_code: '',
      latitude: '',
      longitude: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (location) => {
    setEditingLocation(location);
    setParentLocation(null);
    setFormData({
      name: location.name,
      code: location.code || '',
      type: location.type,
      parent_id: location.parent_id,
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      country: location.country || 'India',
      pin_code: location.pin_code || '',
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || ''
    });
    setDialogOpen(true);
  };

  const getChildType = (parentType) => {
    const typeOrder = ['country', 'state', 'city', 'region', 'warehouse', 'hub', 'store'];
    const idx = typeOrder.indexOf(parentType);
    if (idx >= 0 && idx < typeOrder.length - 1) {
      return typeOrder[idx + 1];
    }
    return 'store';
  };

  const handleSave = async () => {
    if (!formData.name || !formData.type) {
      toast.error('Name and type are required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      if (editingLocation) {
        await controlApi.updateLocation(tenantId, editingLocation.id, payload);
        toast.success('Location updated');
      } else {
        await controlApi.createLocation(tenantId, payload);
        toast.success('Location created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (location) => {
    const children = locations.filter(l => l.parent_id === location.id);
    if (children.length > 0) {
      toast.error('Cannot delete location with children. Remove children first.');
      return;
    }

    if (!confirm(`Delete "${location.name}"?`)) return;

    try {
      await controlApi.updateLocation(tenantId, location.id, { status: 'inactive' });
      toast.success('Location deactivated');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  // Get root-level locations (no parent)
  const rootLocations = locations.filter(l => !l.parent_id && l.status === 'active');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="tenant-locations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/control/tenants/${tenantId}`)}
            className="text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Organization Structure</h1>
            <p className="text-slate-400">{tenant?.name} - Location Hierarchy</p>
          </div>
        </div>
        <Button onClick={() => openCreateDialog(null)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Root Location
        </Button>
      </div>

      {/* Location Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {LOCATION_TYPES.slice(3).map(type => {
          const count = locations.filter(l => l.type === type.value && l.status === 'active').length;
          return (
            <div key={type.value} className="bg-slate-900 rounded-xl p-4 border border-slate-800">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{type.icon}</span>
                <div>
                  <p className="text-2xl font-bold text-white">{count}</p>
                  <p className="text-slate-400 text-sm">{type.label}s</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Location Tree */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
        <h2 className="text-lg font-semibold text-white mb-4">Location Hierarchy</h2>
        
        {rootLocations.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg">No locations defined yet</p>
            <p className="text-sm mb-4">Start by adding a root location (e.g., Region, Zone, State)</p>
            <Button onClick={() => openCreateDialog(null)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add First Location
            </Button>
          </div>
        ) : (
          <div className="space-y-1">
            {rootLocations.map(location => (
              <LocationNode
                key={location.id}
                location={location}
                locations={locations.filter(l => l.status === 'active')}
                level={0}
                onEdit={openEditDialog}
                onDelete={handleDelete}
                onAddChild={openCreateDialog}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingLocation ? 'Edit Location' : parentLocation ? `Add to ${parentLocation.name}` : 'Add Root Location'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingLocation ? 'Update location details' : 'Define a new location in the organization hierarchy'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Delhi Region"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  data-testid="location-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Code</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="DEL-01"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {LOCATION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value} className="text-white">
                      {type.icon} {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="New Delhi"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">State</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="Delhi"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Pin Code</Label>
                <Input
                  value={formData.pin_code}
                  onChange={(e) => setFormData({ ...formData, pin_code: e.target.value })}
                  placeholder="110001"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Latitude</Label>
                <Input
                  type="number"
                  step="0.0000001"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="28.6139"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Longitude</Label>
                <Input
                  type="number"
                  step="0.0000001"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="77.2090"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-location-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingLocation ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
